import { useMemo } from "react";
import useSWR from "swr";
import { getFetcher } from "api/swr";

export type RelatedTemplatesOnlineOpts = {
  idsKey?: "relatedTemplateIds" | "relatedSpineTemplateIds";
};

/**
 * 按 draft 上指定字段拉 `spine-templates/:id` 的 state，判断 401=已上线
 */
export function useRelatedTemplatesOnline(
  draft: any,
  opts?: RelatedTemplatesOnlineOpts,
): {
  isLoading: boolean;
  error: Error | undefined;
  items: any[];
  hasIds: boolean;
} {
  const idsKey = opts?.idsKey ?? "relatedTemplateIds";

  const ids = useMemo((): string[] => {
    const r = draft?.[idsKey];
    return Array.isArray(r) ? r : [];
  }, [draft, idsKey]);

  const { data, isLoading, error } = useSWR(
    ids.length > 0 ? (["related-templates-online", idsKey, ...ids] as const) : null,
    async () => {
      const results = await Promise.all(
        ids.map((rid) =>
          (getFetcher(`/spine-templates/${rid}`) as Promise<any>).catch(
            () => null,
          ),
        ),
      );
      return ids.map((rid, i) => {
        const t: any = results[i];
        if (!t) {
          return { id: rid, name: undefined, state: undefined, isOnline: false };
        }
        const state =
          t.state != null && typeof t.state === "number" ? t.state : undefined;
        return {
          id: rid,
          name: t.name,
          state,
          isOnline: state === 401,
        };
      });
    },
  );

  const items = useMemo(() => {
    if (ids.length === 0) {
      return [];
    }
    return data ?? [];
  }, [ids, data]);

  return {
    isLoading: ids.length > 0 && isLoading,
    error: error as Error | undefined,
    items,
    hasIds: ids.length > 0,
  };
}
